import type { ProgramCenteredListResponse } from "@solomon-apps/shared";
import type { UseMutationResult } from "@tanstack/react-query";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createClientRunProgram, fetchClientEffectivePolicies, fetchClientPrograms } from "@/services/clientPrograms";
import type {
    ClientAssignedProgramView,
    ClientEffectivePolicy,
    ClientProgram,
    CreateClientRunProgramInput
} from "@/types/programCenteredYaas";

import { useClientContextKey } from "./useClientContextKey";

export const CLIENT_PROGRAMS_QUERY_KEY = ["yaas-client", "programs"] as const;
export const CLIENT_EFFECTIVE_POLICIES_QUERY_KEY = ["yaas-client", "effective-policies"] as const;

export function useClientPrograms(): {
    data: ProgramCenteredListResponse<ClientAssignedProgramView> | undefined;
    programs: ClientAssignedProgramView[];
    isLoading: boolean;
    error: Error | null;
} {
    const contextKey = useClientContextKey();
    const { data, isLoading, error } = useQuery<ProgramCenteredListResponse<ClientAssignedProgramView>, Error>({
        queryKey: [...CLIENT_PROGRAMS_QUERY_KEY, contextKey],
        queryFn: () => fetchClientPrograms(),
        staleTime: 2 * 60 * 1000
    });

    return {
        data,
        programs: data?.data ?? [],
        isLoading,
        error
    };
}

export function useClientEffectivePolicies(programUuid: string | null): {
    policies: ClientEffectivePolicy[];
    isLoading: boolean;
    error: Error | null;
} {
    const contextKey = useClientContextKey();
    const { data, isLoading, error } = useQuery<ProgramCenteredListResponse<ClientEffectivePolicy>, Error>({
        queryKey: [...CLIENT_EFFECTIVE_POLICIES_QUERY_KEY, contextKey, programUuid],
        queryFn: () => fetchClientEffectivePolicies(programUuid ?? ""),
        enabled: Boolean(programUuid),
        staleTime: 2 * 60 * 1000
    });

    return {
        policies: data?.data ?? [],
        isLoading,
        error
    };
}

function invalidateProgramQueries(queryClient: ReturnType<typeof useQueryClient>): Promise<unknown[]> {
    return Promise.all([
        queryClient.invalidateQueries({ queryKey: CLIENT_PROGRAMS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: ["client-program-surfaces"] }),
        queryClient.invalidateQueries({ queryKey: ["client-positions"] }),
        queryClient.invalidateQueries({ queryKey: ["program-policies"] })
    ]);
}

export function useCreateClientRunProgram(): UseMutationResult<ClientProgram, Error, CreateClientRunProgramInput> {
    const queryClient = useQueryClient();
    return useMutation<ClientProgram, Error, CreateClientRunProgramInput>({
        mutationFn: createClientRunProgram,
        onSuccess: async () => {
            await invalidateProgramQueries(queryClient);
        }
    });
}
